/// <amd-module name="VSSUI/Components/Button/ExpandableButton" />
import * as React from "react";
import { Expandable, IExpandable, IExpandableChildProps } from "VSSUI/Expandable";
import { Icon, IconSize } from "VSSUI/Icon";
import { css, KeyCode } from "VSSUI/Util";
import { IFocusable } from "VSSUI/Utilities/Focus";
import { Location } from "VSSUI/Utilities/Position";
import { Button } from "./Button";
import { IExpandableButtonProps } from "./ExpandableButton.Props";

let buttonId = 1;

export class ExpandableButton extends React.Component<IExpandableButtonProps> implements IExpandable, IFocusable<{}> {
    public static defaultProps: Readonly<Partial<IExpandableButtonProps>> = {
        expandKey: [KeyCode.downArrow, KeyCode.enter]
    };

    private buttonElement = React.createRef<Button>();
    private containerElement: React.RefObject<HTMLDivElement>;
    private dropdownId: string;
    private expandable = React.createRef<Expandable>();

    public constructor(props: Readonly<IExpandableButtonProps>) {
        super(props);
        this.dropdownId = props.dropdownId || "dropdown-" + buttonId++;
        this.containerElement = props.containerRef || React.createRef<HTMLDivElement>();
    }

    public render(): JSX.Element {
        // We disable the tooltip when we are expanded. Make sure we dont remove it
        // from the component tree, this causes the button element to get regenerated
        // and focus wont return.
        return (
            <Expandable
                disabled={this.props.disabled}
                expandKey={this.props.expandKey}
                onCollapse={this.props.onCollapse}
                onExpand={this.props.onExpand}
                renderCallout={this.renderCallout}
                ref={this.expandable}
            >
                {(expandableProps: IExpandableChildProps) => {
                    return (
                        <div
                            className={css(this.props.className, "bolt-expandable-button inline-flex-row")}
                            onMouseDown={expandableProps.onMouseDown}
                            onKeyDown={expandableProps.onKeyDown}
                            ref={this.containerElement}
                        >
                            <Button
                                {...this.props}
                                ariaControls={expandableProps.expanded ? this.dropdownId : undefined}
                                ariaExpanded={expandableProps.expanded}
                                ariaHasPopup={true}
                                ariaLabel={
                                    this.props.ariaLabel ??
                                    (this.props.tooltipProps && this.props.tooltipProps.text ? this.props.tooltipProps?.text : "")
                                }
                                className={css(
                                    !this.props.text && !this.props.children && this.props.iconProps && "icon-only",
                                    expandableProps.expanded && "active",
                                    this.props.buttonClassName
                                )}
                                onClick={e => {
                                    expandableProps.onClick(e);
                                    if (this.props.onClick) {
                                        this.props.onClick(e);
                                    }
                                    e.preventDefault();
                                }}
                                ref={this.buttonElement}
                                tooltipProps={
                                    this.props.tooltipProps ? { ...this.props.tooltipProps, disabled: expandableProps.expanded } : undefined
                                }
                            >
                                {this.props.children}
                                {!this.props.hideDropdownIcon
                                    ? Icon({
                                          key: "dropdown-icon",
                                          className: "icon-right font-weight-normal",
                                          iconName: "ChevronDownMed",
                                          size: IconSize.small
                                      })
                                    : undefined}
                            </Button>
                        </div>
                    );
                }}
            </Expandable>
        );
    }

    public collapse = (): void => {
        if (this.expandable.current) {
            this.expandable.current.collapse();
        }
    };

    public expand = (): void => {
        if (this.expandable.current) {
            this.expandable.current.expand();
        }
    };

    public focus(): void {
        if (this.buttonElement.current) {
            this.buttonElement.current.focus();
        }
    }

    private renderCallout = () => {
        return this.props.renderCallout(
            this,
            this.dropdownId,
            this.props.anchorElement
                ? this.props.anchorElement
                : !this.props.anchorPoint
                ? this.containerElement.current
                    ? this.containerElement.current
                    : undefined
                : undefined,
            this.props.anchorOffset || { horizontal: 0, vertical: 0 },
            this.props.anchorOrigin || { horizontal: Location.end, vertical: Location.end },
            this.props.anchorPoint,
            this.props.dropdownOrigin || { horizontal: Location.end, vertical: Location.start }
        );
    };
}
